/* SALES OP WEEKLY REPORT (2026-10-24) — the screen over backend/sopWeekly.js. Max: "mỗi thứ 5 team sale sẽ bỏ các file
   vào folder này / sau đó sẽ run code ở mục Run Python Job / khi chạy xong sẽ mở file output lên và refresh lại data ...
   tôi cần bạn thiết kế lại trên portal nhé. về file code thì ko thay đổi logic gì nhé".
   Three steps on one page: 1 the week's 16 files (dropped all at once, each to its row by its name, saved under the name
   the job looks for), 2 the job (the same Python script, its log read back into one line per step, and the item codes it
   stops for filled in right here), 3 the report (what 02_Weekly-Report_NoVBA.xlsx showed after a refresh, drawn from
   01_Output.xlsx). Project Approval's classes, like the other report screens. Grant `auto_jobs_sop_team`. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, Download, Upload, Play, RefreshCw, Trash2, FileSpreadsheet, MinusCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import SopWeeklyConfig from './SopWeeklyConfig';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './SopWeekly.css';

export const SOP_WEEKLY_ON = process.env.REACT_APP_SOP_WEEKLY === '1';
export const SOP_WEEKLY_OFF_MARK = SOP_WEEKLY_ON ? '' : '__sopweekly_off';

const API = `${BACKEND_URL}/api/sop-weekly`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const vn = new Date(d.getTime() + 7 * 3600 * 1000).toISOString();
  return `${vn.slice(8, 10)}/${MONTHS[Number(vn.slice(5, 7)) - 1]}/${vn.slice(0, 4)} ${vn.slice(11, 16)}`;
};
const kb = (b) => (b == null ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const q0 = (v) => (isNum(v) ? Math.round(Number(v)).toLocaleString('en-US') : '—');
const usd = (v) => (isNum(v) ? `${Number(v) < 0 ? '−' : ''}US$ ${Math.round(Math.abs(Number(v))).toLocaleString('en-US')}` : '—');
const call = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
};
const GROUPS = ['sap', 'eusu', 'exf', 'ar'];
const STEPS = ['sap', 'tpl', 'eusu', 'exf', 'ar'];

/* ---------------------------------------------------------------- 1 · files */
function Files({ t, st, edit, reload, flash }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [results, setResults] = useState([]);
  const pickAll = useRef(null);
  const rowPick = useRef(null);
  const [rowSlot, setRowSlot] = useState(null);

  const upload = async (files, slot) => {
    if (!files.length) return;
    setBusy(true);
    const out = [];
    for (const f of files) {
      const qs = new URLSearchParams({ week: st.week, name: f.name, ...(slot ? { slot } : {}) });
      try {
        const r = await fetch(`${API}/file?${qs}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        out.push({ name: f.name, ok: true, text: j.note ? `${t('xSopw.savedAs', { file: j.file })} ${j.note}` : t('xSopw.savedAs', { file: j.file }) });
      } catch (e) { out.push({ name: f.name, ok: false, text: e.message }); }
    }
    setResults(out);
    setBusy(false);
    reload();
  };
  const remove = async (name) => {
    if (!window.confirm(t('xSopw.removeAsk', { name }))) return;
    try { await call('DELETE', `/file?${new URLSearchParams({ week: st.week, name })}`); reload(); } catch (e) { flash(e.message, true); }
  };
  const have = st.slots.filter((s) => s.file).length;

  return (
    <div className="pa-section">
      {edit && (
        <div className={`sw-drop${over ? ' over' : ''}${busy ? ' busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) upload([...e.dataTransfer.files]); }}>
          <Upload size={22} />
          <div><b>{busy ? t('xSopw.uploading') : t('xSopw.dropTitle', { week: st.week })}</b><span>{t('xSopw.dropHint')}</span></div>
          <button type="button" className="pa-btn primary" disabled={busy} onClick={() => pickAll.current?.click()}>{t('xSopw.chooseFiles')}</button>
          <input ref={pickAll} type="file" multiple accept=".xlsx,.xls,.xlsm" hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} />
        </div>
      )}
      <input ref={rowPick} type="file" accept=".xlsx,.xls,.xlsm" hidden onChange={(e) => { upload([...e.target.files], rowSlot); e.target.value = ''; }} />
      {results.length > 0 && (
        <ul className="sw-results">
          {results.map((r, i) => <li key={i} className={r.ok ? 'ok' : 'bad'}>{r.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} <b>{r.name}</b> — {r.text}</li>)}
          <li><button type="button" className="pa-btn small" onClick={() => setResults([])}>{t('xSopw.dismiss')}</button></li>
        </ul>
      )}
      <div className="sw-count">{t('xSopw.filesCount', { n: have, total: st.slots.length, week: st.week })}</div>
      <div className="sw-wrap">
        <table className="pa-table sw-files">
          <thead><tr>
            <th>{t('xSopw.colFile')}</th><th>{t('xSopw.colStatus')}</th><th>{t('xSopw.colSaved')}</th>
            <th className="num">{t('xSopw.colSize')}</th><th>{t('xSopw.colUploaded')}</th><th className="sw-col-action">{t('xSopw.colAction')}</th>
          </tr></thead>
          <tbody>
            {GROUPS.map((g) => (
              <React.Fragment key={g}>
                <tr className="sw-group"><td colSpan={6}>{t(`xSopw.group_${g}`)}</td></tr>
                {st.slots.filter((s) => s.group === g).map((s) => (
                  <tr key={s.key} data-slot={s.key}>
                    <td className="sw-name">{s.label}</td>
                    <td>{!s.file ? <span className="sw-chip miss"><MinusCircle size={13} /> {t('xSopw.missing')}</span>
                      : !s.tagOk ? <span className="sw-chip warn" title={t('xSopw.tagHint')}><AlertTriangle size={13} /> {t('xSopw.otherWeek', { tag: s.tag || '?' })}</span>
                        : <span className="sw-chip ok"><CheckCircle2 size={13} /> {t('xSopw.ready')}</span>}
                      {s.count > 1 && <span className="sw-chip warn" title={t('xSopw.twoHint')}>{t('xSopw.twoFiles', { n: s.count })}</span>}
                    </td>
                    <td className="sw-saved">{s.file || ''}{s.original && s.original !== s.file && <span className="sw-sub">{t('xSopw.originalName', { name: s.original })}</span>}</td>
                    <td className="num">{kb(s.size)}</td>
                    <td className="sw-when">{s.file ? (s.uploadedBy ? `${s.uploadedBy} · ${fmtWhen(s.uploadedAt)}` : <span title={t('xSopw.fromFolderHint')}>{t('xSopw.fromFolder')} · {fmtWhen(s.modified)}</span>) : ''}</td>
                    <td className="sw-col-action">
                      {edit && <button type="button" className="pa-btn small" disabled={busy} onClick={() => { setRowSlot(s.key); setTimeout(() => rowPick.current?.click(), 0); }}><Upload size={13} /> {s.file ? t('xSopw.replace') : t('xSopw.upload')}</button>}
                      {s.file && <a className="pa-btn small" href={`${API}/file?${new URLSearchParams({ week: st.week, name: s.file })}`}><Download size={13} /></a>}
                      {edit && s.file && <button type="button" className="pa-btn small danger" disabled={busy} title={t('xSopw.remove')} onClick={() => remove(s.file)}><Trash2 size={13} /></button>}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {st.extras.length > 0 && (
        <div className="sw-extras">
          <h4>{t('xSopw.extrasTitle')}</h4>
          <p className="sw-hint">{t('xSopw.extrasHint')}</p>
          <ul>{st.extras.map((x) => <li key={x.file}><FileSpreadsheet size={13} /> {x.file} <span className="sw-sub">{kb(x.size)}</span>{edit && <button type="button" className="pa-btn small danger" onClick={() => remove(x.file)}><Trash2 size={12} /></button>}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- 2 · run */
function StepList({ t, summary }) {
  if (!summary) return null;
  return (
    <div className="sw-steps">
      {STEPS.map((k) => {
        const s = summary.steps?.[k] || { status: 'not run' };
        const Icon = s.status === 'ok' ? CheckCircle2 : s.status === 'failed' ? XCircle : MinusCircle;
        return (
          <div key={k} className={`sw-step ${s.status === 'ok' ? 'ok' : s.status === 'failed' ? 'bad' : 'idle'}`}>
            <Icon size={16} />
            <b>{t(`xSopw.step_${k}`)}</b>
            <span>{s.status === 'ok' ? t('xSopw.stepOk') : s.status === 'failed' ? s.message : t('xSopw.stepNotRun')}</span>
          </div>
        );
      })}
    </div>
  );
}

function ItemCodes({ t, edit, onSaved, flash }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { call('GET', '/config/missing').then(setData).catch((e) => flash(e.message, true)); }, [flash]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="sw-hint">{t('xSopw.loading')}</p>;
  if (!data.items.length) return <p className="sw-okline"><CheckCircle2 size={14} /> {t('xSopw.codesNone')}</p>;
  const val = (it, k) => (draft[it.itemCode]?.[k] !== undefined ? draft[it.itemCode][k] : (it[k] ?? ''));
  const set = (it, k, v) => setDraft((d) => ({ ...d, [it.itemCode]: { ...d[it.itemCode], [k]: v } }));
  const ready = data.items.filter((it) => val(it, 'category') && isNum(val(it, 'price')));
  const save = async () => {
    setBusy(true);
    try {
      const r = await call('PUT', '/config/items', { items: ready.map((it) => ({ itemCode: it.itemCode, category: val(it, 'category'), price: Number(val(it, 'price')) })) });
      flash(t('xSopw.codesSaved', { n: r.saved.length }));
      setDraft({});
      load();
      onSaved?.();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  };
  return (
    <div className="sw-codes">
      <h4><AlertTriangle size={15} /> {t('xSopw.codesTitle', { n: data.items.length })}</h4>
      <p className="sw-hint">{t('xSopw.codesHint')}</p>
      <div className="sw-wrap">
        <table className="pa-table sw-codetable">
          <thead><tr><th>{t('xSopw.colItem')}</th><th>{t('xSopw.colProduct')}</th><th>{t('xSopw.colCategory')}</th><th className="num">{t('xSopw.colPrice')}</th></tr></thead>
          <tbody>{data.items.map((it) => (
            <tr key={it.itemCode} data-code={it.itemCode}>
              <td className="sw-code">{it.itemCode}</td>
              <td>{it.product || it.name || ''}</td>
              <td><select disabled={!edit} value={val(it, 'category')} onChange={(e) => set(it, 'category', e.target.value)}>
                <option value="">—</option>{data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></td>
              <td className="num"><input disabled={!edit} inputMode="decimal" value={val(it, 'price')} onChange={(e) => set(it, 'price', e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && <div className="pa-toolbar"><button type="button" className="pa-btn primary" disabled={busy || !ready.length} onClick={save}>{t('xSopw.codesSave', { n: ready.length })}</button></div>}
    </div>
  );
}

function Run({ t, st, edit, reload, flash }) {
  const [log, setLog] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState(null);
  const [codesKey, setCodesKey] = useState(0);
  const logRef = useRef(null);
  const have = st.slots.filter((s) => s.file).length;
  const latest = st.runs[0];
  const shown = view || (result ? { summary: result.summary, status: result.status, log } : latest ? { ...latest, fromHistory: true } : null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const run = async () => {
    if (have < st.slots.length && !window.confirm(t('xSopw.runMissingAsk', { n: st.slots.length - have }))) return;
    setBusy(true); setView(null); setResult(null); setLog(''); flash(null);
    let text = '';
    try {
      const r = await fetch(`${API}/run`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ week: st.week }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        text += dec.decode(value, { stream: true });
        setLog(text.replace(/\n?__RESULT__ .*$/s, ''));
      }
      const m = /__RESULT__ (\{.*\})/.exec(text);
      const res = m ? JSON.parse(m[1]) : null;
      if (res?.runPk) {
        const full = await call('GET', `/run/${res.runPk}`);
        setResult({ ...res, summary: full.summary });
      }
    } catch (e) { flash(e.message, true); }
    setBusy(false);
    setCodesKey((k) => k + 1);
    reload();
  };
  const openRun = async (pk) => {
    try { const r = await call('GET', `/run/${pk}`); setView(r); setLog(r.log || ''); } catch (e) { flash(e.message, true); }
  };

  return (
    <div className="pa-section">
      <div className="sw-runbar">
        <div className="sw-ready">
          {have === st.slots.length ? <span className="sw-chip ok"><CheckCircle2 size={13} /> {t('xSopw.allFiles', { n: have })}</span>
            : <span className="sw-chip warn"><AlertTriangle size={13} /> {t('xSopw.someMissing', { n: st.slots.length - have, total: st.slots.length })}</span>}
          {!st.mayRun && <span className="sw-chip miss" title={t('xSopw.onlyCurrentHint')}>{t('xSopw.onlyCurrent', { week: st.current.label })}</span>}
        </div>
        {edit && <button type="button" className="pa-btn primary sw-runbtn" disabled={busy || !st.mayRun || !!st.running} onClick={run}>
          <Play size={15} /> {busy || st.running ? t('xSopw.running') : t('xSopw.runJob', { week: st.current.label })}</button>}
      </div>
      {st.running && !busy && <p className="sw-hint">{t('xSopw.runningBy', { by: st.running.by || '', when: fmtWhen(st.running.startedAt) })}</p>}

      {shown && !busy && (
        <div className={`sw-result ${shown.status}`}>
          <div className="sw-result-head">
            {shown.status === 'ok' ? <CheckCircle2 size={18} /> : shown.status === 'partial' ? <AlertTriangle size={18} /> : <XCircle size={18} />}
            <b>{t(`xSopw.result_${shown.status}`)}</b>
            {shown.run_pk && <span className="sw-sub">{t('xSopw.runOf', { n: shown.run_pk, week: shown.week_label, by: shown.started_by || '', when: fmtWhen(shown.started_at) })}</span>}
            {result?.seconds != null && !view && <span className="sw-sub">{t('xSopw.took', { s: result.seconds })}</span>}
          </div>
          <StepList t={t} summary={shown.summary} />
          {shown.summary?.warnings?.length > 0 && (
            <details className="sw-warn" open={shown.summary.warnings.some((w) => /ACTION REQUIRED/.test(w))}>
              <summary>{t('xSopw.warnings', { n: shown.summary.warnings.length })}</summary>
              <ul>{shown.summary.warnings.map((w, i) => <li key={i} className={/ACTION REQUIRED/.test(w) ? 'act' : ''}>{w}</li>)}</ul>
            </details>
          )}
          {shown.status === 'ok' && <p className="sw-okline"><CheckCircle2 size={14} /> {t('xSopw.goReport')}</p>}
        </div>
      )}
      {(shown?.summary?.missingItems || (!shown && false)) && !busy && <ItemCodes key={codesKey} t={t} edit={edit} flash={flash} onSaved={reload} />}

      {(busy || log) && (
        <details className="sw-logbox" open={busy}>
          <summary>{busy ? t('xSopw.liveLog') : t('xSopw.fullLog')}</summary>
          <pre ref={logRef}>{log || t('xSopw.starting')}</pre>
        </details>
      )}

      <h4 className="sw-h">{t('xSopw.history')}</h4>
      {st.runs.length === 0 ? <p className="sw-hint">{t('xSopw.noRuns')}</p> : (
        <div className="sw-wrap">
          <table className="pa-table sw-runs">
            <thead><tr><th>#</th><th>{t('xSopw.colWeek')}</th><th>{t('xSopw.colStarted')}</th><th>{t('xSopw.colBy')}</th><th>{t('xSopw.colResult')}</th><th>{t('xSopw.colSteps')}</th><th className="sw-col-action">{t('xSopw.colAction')}</th></tr></thead>
            <tbody>{st.runs.map((r) => {
              const okN = r.summary ? STEPS.filter((k) => r.summary.steps?.[k]?.status === 'ok').length : null;
              return (
                <tr key={r.run_pk} className={view?.run_pk === r.run_pk ? 'sw-on' : ''}>
                  <td>{r.run_pk}</td><td>{r.week_label}</td><td>{fmtWhen(r.started_at)}</td><td>{r.started_by || ''}</td>
                  <td><span className={`sw-chip ${r.status === 'ok' ? 'ok' : r.status === 'running' ? 'miss' : r.status === 'partial' ? 'warn' : 'bad'}`}>{t(`xSopw.result_${r.status}`)}</span></td>
                  <td>{okN == null ? '' : `${okN}/${STEPS.length}`}{r.summary?.missingItems ? ` · ${t('xSopw.askedCodes')}` : ''}</td>
                  <td className="sw-col-action"><button type="button" className="pa-btn small" onClick={() => openRun(r.run_pk)}>{t('xSopw.viewLog')}</button></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- 3 · report */
const byKey = (rows, k) => Object.fromEntries((rows || []).map((r) => [r[k], r]));
/* "Total" first, then the order the output holds — the workbook's pivots list them the same way */
const orderKeys = (cur, prev, k) => {
  const keys = [];
  for (const r of [...(cur || []), ...(prev || [])]) if (!keys.includes(r[k])) keys.push(r[k]);
  return keys.sort((a, b) => (a === 'Total' ? -1 : b === 'Total' ? 1 : 0));
};

/* 3PL + EUSU by category, as the workbook's 07_Inventory (the two appended) */
function sumInv(a, b) {
  const out = {};
  for (const r of [...(a || []), ...(b || [])]) {
    const o = out[r.category] || (out[r.category] = { category: r.category });
    for (const [k, v] of Object.entries(r)) if (k !== 'category' && isNum(v)) o[k] = (o[k] || 0) + Number(v);
  }
  return Object.values(out);
}
/* THE WORKBOOK'S CHARTS ON THE SCREEN (Max, 29/10: "cái tab Report thì thể hiện các chart này … số liệu trong hình nếu có
   thay đổi thì màu đỏ còn không là đen") — EXF status, account receivables and the four stock charts of
   02_Weekly-Report, each over a small table: a figure that differs from last week's is red, an unchanged one black. */
const wk = (n) => `W${String(n).padStart(2, '0')}`;
// the charts' tables are narrow: $526,791 rather than US$ 526,791 (the title says USD)
const usd$ = (v) => (isNum(v) ? `${Number(v) < 0 ? '−' : ''}$${Math.round(Math.abs(Number(v))).toLocaleString('en-US')}` : '—');
const same = (a, b) => isNum(a) && isNum(b) && Math.round(Number(a)) === Math.round(Number(b));
const kfmt = (money) => (v) => {
  const a = Math.abs(v); const sgn = v < 0 ? '−' : '';
  const n = a >= 1e6 ? `${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M` : a >= 1e3 ? `${Math.round(a / 1e3)}K` : String(Math.round(a));
  return `${sgn}${money ? '$' : ''}${n}`;
};
const useInk = () => {
  const dark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  return { dark, ink: dark ? '#a3a3a3' : '#52514e', grid: dark ? '#2f2f2f' : '#eef0f3' };
};
const STOCK_ORDER = ['Total', 'Chair', 'Table', 'Cot', 'Storage', 'ACC-Cot', 'ACC-Chair', 'Other'];
const ordered = (keys, order) => [...order.filter((k) => keys.includes(k)), ...keys.filter((k) => !order.includes(k))];

/* one row of a chart's table; `red` decides per cell */
function FigRow({ label, swatch, keys, val, red, money }) {
  return (
    <tr>
      <td className="sw-figlab">{swatch && <i style={{ background: swatch }} />}{label}</td>
      {keys.map((k) => <td key={k} className={`num${red(k) ? ' sw-red' : ''}`}>{money ? usd$(val(k)) : q0(val(k))}</td>)}
    </tr>
  );
}

function ExfChart({ t, sec, nameOf }) {
  const { ink, grid } = useInk();
  if (!sec || !sec.cur.length) return null;
  const cur = byKey(sec.cur, 'entity'); const prev = byKey(sec.prev, 'entity');
  const keys = orderKeys(sec.cur, sec.prev, 'entity').filter((k) => cur[k]);
  const data = keys.map((k) => ({ name: nameOf(k), inProduction: Number(cur[k].inProduction) || 0, exFactory: Number(cur[k].exFactory) || 0 }));
  const C = { inProduction: '#1c5cab', exFactory: '#86b6ef' };
  return (
    <div className="sw-fig" data-sw-chart="exf">
      <div className="sw-chart-title">{t('xSopw.chExf')}</div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 4 }} barCategoryGap="28%">
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: ink }} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: ink }} tickFormatter={kfmt(true)} width={60} />
          <Tooltip formatter={(v, n) => [usd(v), n]} cursor={{ fill: 'rgba(18,59,109,.06)' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="inProduction" stackId="a" name={t('xSopw.m_inProduction')} fill={C.inProduction} />
          <Bar dataKey="exFactory" stackId="a" name={t('xSopw.m_exFactory')} fill={C.exFactory} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="sw-wrap"><table className="pa-table sw-figtab">
        <thead><tr><th />{keys.map((k) => <th key={k} className="num">{nameOf(k)}</th>)}</tr></thead>
        <tbody>
          <FigRow label={t('xSopw.m_exFactory')} swatch={C.exFactory} keys={keys} money val={(k) => cur[k]?.exFactory} red={(k) => !same(cur[k]?.exFactory, prev[k]?.exFactory)} />
          <FigRow label={t('xSopw.m_inProduction')} swatch={C.inProduction} keys={keys} money val={(k) => cur[k]?.inProduction} red={(k) => !same(cur[k]?.inProduction, prev[k]?.inProduction)} />
        </tbody>
      </table></div>
    </div>
  );
}

function ArChart({ t, sec, nameOf }) {
  const { ink, grid } = useInk();
  if (!sec || !sec.cur.length) return null;
  const cur = byKey(sec.cur, 'entity'); const prev = byKey(sec.prev, 'entity');
  const keys = orderKeys(sec.cur, sec.prev, 'entity').filter((k) => cur[k]);
  const data = keys.map((k) => ({ name: nameOf(k), paid: Number(cur[k].paid) || 0, onTrack: Number(cur[k].onTrack) || 0, overdue: Number(cur[k].overdue) || 0 }));
  const C = { paid: '#0ca30c', onTrack: '#2a78d6', overdue: '#fab219' };
  const pctOf = (row, k) => { const tot = row.paid + row.onTrack + row.overdue; return tot ? `${((row[k] / tot) * 100).toFixed(1)}%` : '—'; };
  return (
    <div className="sw-fig" data-sw-chart="ar">
      <div className="sw-chart-title">{t('xSopw.chAr')}</div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} stackOffset="expand" margin={{ top: 8, right: 12, left: 8, bottom: 4 }} barCategoryGap="35%">
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: ink }} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: ink }} tickFormatter={(v) => `${Math.round(v * 100)}%`} width={44} />
          <Tooltip formatter={(v, n, it) => [`${usd(v)} · ${pctOf(it.payload, it.dataKey)}`, n]} cursor={{ fill: 'rgba(18,59,109,.06)' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="paid" stackId="a" name={t('xSopw.m_paid')} fill={C.paid} />
          <Bar dataKey="onTrack" stackId="a" name={t('xSopw.m_onTrack')} fill={C.onTrack} />
          <Bar dataKey="overdue" stackId="a" name={t('xSopw.m_overdue')} fill={C.overdue} />
        </BarChart>
      </ResponsiveContainer>
      <div className="sw-wrap"><table className="pa-table sw-figtab">
        <thead><tr><th />{keys.map((k) => <th key={k} className="num">{nameOf(k)}</th>)}</tr></thead>
        <tbody>
          {['overdue', 'onTrack', 'paid'].map((m) => (
            <FigRow key={m} label={t(`xSopw.m_${m}`)} swatch={C[m]} keys={keys} money val={(k) => cur[k]?.[m]} red={(k) => !same(cur[k]?.[m], prev[k]?.[m])} />
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

/* the four stock charts: last week beside this week per category, 3PL + EUSU together (the workbook's 07_Inventory) */
function StockCharts({ t, cur: curRows, prev: prevRows, week, lastWeek, nameOf }) {
  const { ink, grid } = useInk();
  if (!curRows.length && !prevRows.length) return null;
  const cur = byKey(curRows, 'category'); const prev = byKey(prevRows, 'category');
  const keys = ordered(orderKeys(curRows, prevRows, 'category'), STOCK_ORDER);
  const charts = [
    { key: 'reservedQty', title: 'chResQty', light: '#a9cbf3', dark: '#2a78d6' },
    { key: 'reservedAmt', title: 'chResAmt', light: '#a9cbf3', dark: '#2a78d6', money: true },
    { key: 'uncommittedQty', title: 'chUncQty', light: '#f6c5a8', dark: '#e0631f' },
    { key: 'uncommittedAmt', title: 'chUncAmt', light: '#f6c5a8', dark: '#e0631f', money: true },
  ];
  return (
    <div className="sw-figgrid">
      {charts.map((c) => {
        const data = keys.map((k) => ({ name: nameOf(k), prev: Number(prev[k]?.[c.key]) || 0, cur: Number(cur[k]?.[c.key]) || 0 }));
        return (
          <div key={c.key} className="sw-fig" data-sw-chart={c.key}>
            <div className="sw-chart-title">{t(`xSopw.${c.title}`)}</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 4 }} barGap={2} barCategoryGap="22%">
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: ink }} interval={0} />
                <YAxis tick={{ fontSize: 10.5, fill: ink }} tickFormatter={kfmt(c.money)} width={52} />
                <Tooltip formatter={(v, n) => [c.money ? usd(v) : q0(v), n]} cursor={{ fill: 'rgba(18,59,109,.06)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="prev" name={wk(lastWeek)} fill={c.light} radius={[3, 3, 0, 0]} />
                <Bar dataKey="cur" name={wk(week)} fill={c.dark} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="sw-wrap"><table className="pa-table sw-figtab">
              <thead><tr><th />{keys.map((k) => <th key={k} className="num">{nameOf(k)}</th>)}</tr></thead>
              <tbody>
                <FigRow label={wk(lastWeek)} swatch={c.light} keys={keys} money={c.money} val={(k) => prev[k]?.[c.key]} red={() => false} />
                <FigRow label={wk(week)} swatch={c.dark} keys={keys} money={c.money} val={(k) => cur[k]?.[c.key]} red={(k) => !same(cur[k]?.[c.key], prev[k]?.[c.key])} />
              </tbody>
            </table></div>
          </div>
        );
      })}
    </div>
  );
}

function Report({ t, st, flash }) {
  const [week, setWeek] = useState(st.week);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    call('GET', `/report?week=${encodeURIComponent(week)}`).then((d) => { if (live) setData(d); }).catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [week, st.output?.modified]);
  const weeks = useMemo(() => {
    const list = (data?.weeks || []).map((w) => `W${String(w.week).padStart(2, '0')}-${w.year}`);
    return list.includes(week) ? list : [week, ...list];
  }, [data, week]);
  const s = data?.sections;
  const empty = s && !s.sap.cur.length && !s.tpl.cur.length && !s.eusu.cur.length && !s.exf.cur.length && !s.ar.cur.length;

  return (
    <div className="pa-section">
      <div className="pa-toolbar sw-repbar">
        <label className="sw-field"><span>{t('xSopw.reportWeek')}</span>
          <select value={week} onChange={(e) => setWeek(e.target.value)}>{weeks.map((w) => <option key={w} value={w}>{w}</option>)}</select>
        </label>
        {data && <span className="sw-sub">{t('xSopw.vsWeek', { week: `W${String(data.lastWeek).padStart(2, '0')}-${data.lastYear}` })} · {t('xSopw.outputAt', { when: fmtWhen(data.outputModified) })}</span>}
        <span className="sw-grow" />
        <a className="pa-btn small" href={`${API}/download/output`}><Download size={13} /> 01_Output.xlsx</a>
        <a className="pa-btn small" href={`${API}/download/report`} title={t('xSopw.excelReportHint')}><Download size={13} /> 02_Weekly-Report</a>
        {/* Allie's three slides with this week's figures (task #44) — her charts, refilled; the workbook's own only refresh on
            the Z: drive. Max 01/11: as close to her deck as possible, so PowerPoint rather than the earlier Excel copy. */}
        <a className="pa-btn small" href={`${API}/charts-pptx?week=${encodeURIComponent(week)}`} title={t('xSopw.chartsPptHint')} data-sw-charts-pptx>
          <Download size={13} /> {t('xSopw.chartsPpt')}</a>
      </div>
      {err && <p className="sw-hint sw-neg">{err}</p>}
      {!data && !err && <p className="sw-hint">{t('xSopw.loading')}</p>}
      {empty && <p className="sw-empty">{t('xSopw.weekEmpty', { week })}</p>}
      {/* ONLY WHAT THE TEAM'S SLIDES CARRY (Max, 29/10: "mấy cái phần ko có trong slide tui gửi thì bỏ đi nhé") — EXF Status,
          Account Receivables, the four stock charts; the tables of figures under each chart are the slides' own. */}
      {s && !empty && (
        <div className="sw-sections">
          {/* half width each, side by side (Max, 29/10: "giảm width lại còn 1 nửa để còn chụp hình bỏ vào slide") */}
          <div className="sw-figgrid">
            <ExfChart t={t} sec={s.exf} nameOf={(k) => (k === 'Total' ? t('xSopw.total') : k)} />
            <ArChart t={t} sec={s.ar} nameOf={(k) => (k === 'Total' ? t('xSopw.total') : k)} />
          </div>
          <StockCharts t={t} cur={sumInv(s.tpl.cur, s.eusu.cur)} prev={sumInv(s.tpl.prev, s.eusu.prev)} week={data.week} lastWeek={data.lastWeek}
            nameOf={(k) => (k === 'Total' ? t('xSopw.total') : k)} />
        </div>
      )}
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The three steps are state inside the screen, so a step that lives on another
   one clicks that step's own button first (`prepare`, data-sw-tab); the tour opens on Files (onBeforeTour). Run's button is
   drawn only for people who may run, so the step points at the whole run bar. Config is left to its own screen text. */
const clickStep = (k) => () => document.querySelector(`[data-sw-tab="${k}"]`)?.click();
const TOUR = [
  { selector: '.sw-weekbar', title: 'tourSopWeekly.weekT', body: 'tourSopWeekly.weekB' },
  { selector: '.sw-ladder', title: 'tourSopWeekly.stepsT', body: 'tourSopWeekly.stepsB' },
  { selector: '.sw-drop', title: 'tourSopWeekly.dropT', body: 'tourSopWeekly.dropB', prepare: clickStep('files') },
  { selector: '.sw-files', title: 'tourSopWeekly.filesT', body: 'tourSopWeekly.filesB', prepare: clickStep('files') },
  { selector: '.sw-runbar', title: 'tourSopWeekly.runT', body: 'tourSopWeekly.runB', prepare: clickStep('run') },
  { selector: '.sw-repbar', title: 'tourSopWeekly.reportT', body: 'tourSopWeekly.reportB', prepare: clickStep('report') },
];

/* ---------------------------------------------------------------- page */
export default function SopWeekly() {
  const { t } = useTranslation();
  const qs0 = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(qs0.get('tab') || 'files');
  const [week, setWeek] = useState(qs0.get('week') || '');
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState(null);
  const flash = useCallback((text, bad) => setMsg(text ? { text, bad } : null), []);
  const reload = useCallback(async () => {
    try { const s = await call('GET', `/state${week ? `?week=${encodeURIComponent(week)}` : ''}`); setSt(s); if (!week) setWeek(s.week); }
    catch (e) { flash(e.message, true); }
  }, [week, flash]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (!st?.running) return undefined; const id = setInterval(reload, 5000); return () => clearInterval(id); }, [st?.running, reload]);
  const pick = (k) => { setTab(k); const u = new URL(window.location.href); u.searchParams.set('tab', k); window.history.replaceState(null, '', u); };
  const pickWeek = (w) => { setWeek(w); const u = new URL(window.location.href); u.searchParams.set('week', w); window.history.replaceState(null, '', u); };
  const edit = !!st?.edit;
  const have = st ? st.slots.filter((s) => s.file).length : 0;
  const last = st?.runs?.find((r) => r.week_label === st.week);

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main sw-page">
        <div className="mh-titlerow">
          <h2 className="pa-title">{t('xSopw.title')}</h2>
          <ModuleHelp steps={TOUR} tourKey="sopWeekly" ready={!!st} onBeforeTour={() => pick('files')} />
        </div>
        {msg && <div className={`sw-flash${msg.bad ? ' bad' : ''}`} role="status">{msg.text} <button type="button" className="pa-btn small" onClick={() => setMsg(null)}>×</button></div>}
        {!st ? <p className="sw-hint">{t('xSopw.loading')}</p> : (
          <>
            <div className="pa-toolbar sw-weekbar">
              <label className="sw-field"><span>{t('xSopw.week')}</span>
                <select value={st.week} onChange={(e) => pickWeek(e.target.value)}>
                  {st.weeks.map((w) => <option key={w} value={w}>{w}{w === st.current.label ? ` · ${t('xSopw.thisWeekTag')}` : ''}</option>)}
                </select>
              </label>
              {st.env === 'staging' && <span className="sw-chip miss" title={t('xSopw.sandboxHint')}>{t('xSopw.sandbox')}</span>}
              <span className="sw-grow" />
              <button type="button" className="pa-btn small" title={t('xSopw.refresh')} onClick={reload}><RefreshCw size={13} /></button>
            </div>
            <div className="sw-ladder" role="tablist">
              {[
                ['files', `1 · ${t('xSopw.tabFiles')}`, `${have}/${st.slots.length}`, have === st.slots.length ? 'ok' : 'warn'],
                ['run', `2 · ${t('xSopw.tabRun')}`, st.running ? t('xSopw.running') : last ? t(`xSopw.result_${last.status}`) : t('xSopw.notRun'), st.running ? 'warn' : last?.status === 'ok' ? 'ok' : last ? 'bad' : 'idle'],
                ['report', `3 · ${t('xSopw.tabReport')}`, st.output ? fmtWhen(st.output.modified) : '—', 'idle'],
              ].map(([k, label, badge, tone]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={`sw-rung${tab === k ? ' active' : ''}`} onClick={() => pick(k)} data-sw-tab={k}>
                  <span>{label}</span><em className={`sw-badge ${tone}`}>{badge}</em>
                </button>
              ))}
              {/* not a weekly step: the lookups the job reads every week, changed only when something new appears */}
              <span className="sw-ladder-sep" aria-hidden="true" />
              <button type="button" role="tab" aria-selected={tab === 'config'} className={`sw-rung sw-rung-cfg${tab === 'config' ? ' active' : ''}`} onClick={() => pick('config')}>
                <span>{t('xSopw.tabConfig')}</span><em className="sw-badge idle">00_Config.xlsx</em>
              </button>
            </div>
            {tab === 'files' && <Files t={t} st={st} edit={edit} reload={reload} flash={flash} />}
            {tab === 'run' && <Run t={t} st={st} edit={edit} reload={reload} flash={flash} />}
            {tab === 'report' && <Report key={st.week} t={t} st={st} flash={flash} />}
            {tab === 'config' && <SopWeeklyConfig t={t} edit={edit} flash={flash} running={!!st.running} />}
          </>
        )}
      </div>
    </div>
  );
}
